import { Editor, EditorContent } from '@tiptap/react';
import clsx from 'clsx';
import { ActionData, Toolbar, ToolbarProvider } from 'copilot-design-system';
import { useRef, useState } from 'react';

import { EDITOR_ACTIONS_CONFIG } from 'src/components/tiptap/constants';
import { LinkPopover } from 'src/components/tiptap/editor-menu/LinkPopover';
import { HighlightToolbar } from 'src/components/tiptap/highlight-menu/HighlightToolbar';
import { useEditorActions } from 'src/components/tiptap/hooks/useEditorActions';
import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';

/**
 * RichTextInput component is a wrapper around the tiptap editor that provides a toolbar and highlight menu.
 * @param editor - The tiptap editor instance
 * @param classes - The classes to apply to the container and toolbar
 * @param supportActions - The actions to support in the toolbar and highlight menu
 * @param showTopToolbar - Whether to show the top toolbar
 * @returns
 */
export const RichTextInput = ({
  editor,
  classes,
  supportActions,
  showTopToolbar = true,
}: {
  editor: Editor;
  classes?: {
    container?: string;
    toolbar?: string;
  };
  supportActions: { toolbar?: string[]; highlight?: string[] };
  showTopToolbar?: boolean;
}) => {
  const { getActionState, onAction } = useEditorActions(editor);

  const highlightToolbarRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /**
   * Handle the action formatting or if the action is
   * a non formatting action such as link, it handles
   * showing the link form popover.
   * @param actionData - The action data to handle
   */
  const handleAction = (actionData: ActionData) => {
    if (actionData.action === 'link') {
      setAnchorEl(highlightToolbarRef.current);
    } else {
      onAction(actionData);
    }
  };
  return (
    <ToolbarProvider
      config={EDITOR_ACTIONS_CONFIG}
      onAction={handleAction}
      getActionState={getActionState}
    >
      <HighlightToolbar
        ref={highlightToolbarRef}
        tiptapEditor={editor}
        overrideActions={supportActions.highlight}
      />
      {showTopToolbar && (
        <Toolbar
          variant="sticky"
          overrideActions={supportActions.toolbar}
          className={clsx(classes?.toolbar, 'w-[-webkit-fill-available]')}
        />
      )}
      <RichTextStyles>
        <div className={classes?.container}>
          <EditorContent editor={editor} />
        </div>
      </RichTextStyles>
      <LinkPopover
        editor={editor}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
      />
    </ToolbarProvider>
  );
};
